import {
  QueryClient,
  QueryClientProvider,
  useQueryClient,
} from '@tanstack/react-query'
import { useState, useEffect, useRef } from 'react'
import { Toaster } from 'sonner'
import { QUERY_STALE_TIME } from '@/lib/constants'
import { convertToMilliseconds } from '@/lib/utils'
import { useCurrentLocale } from '@/locales/client'
import { TooltipProvider } from '@tameeni/ui'
import { AuthProvider, PostLoginTracker } from '@/features/authentication'

type GlobalProvidersProps = {
  readonly children: React.ReactNode
}

/**
 * Refetches every active query whenever the language changes, so backend-
 * localised data comes back in the new locale. Combined with the per-request
 * Accept-Language header in useClientApi, this simulates a page refresh on
 * language switch without a hard browser reload.
 */
function LocaleQuerySync({ locale }: { locale: string }) {
  const queryClient = useQueryClient()
  const previousLocale = useRef(locale)

  useEffect(() => {
    if (previousLocale.current === locale) return
    previousLocale.current = locale
    // No filter → invalidate ALL queries; active ones refetch immediately,
    // inactive ones on their next mount.
    void queryClient.invalidateQueries()
  }, [locale, queryClient])

  return null
}

export default function GlobalProviders({ children }: GlobalProvidersProps) {
  const locale = useCurrentLocale()

  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            retry: false,
            refetchOnWindowFocus: false,
            staleTime: convertToMilliseconds(QUERY_STALE_TIME, 'minutes'),
          },
        },
      })
  )

  return (
    <AuthProvider locale={locale}>
      <PostLoginTracker />
      <QueryClientProvider client={queryClient}>
        <LocaleQuerySync locale={locale} />
        <PostLoginTracker />
        <TooltipProvider>
          {children}
          <Toaster
            richColors
            position={locale === 'ar' ? 'bottom-right' : 'bottom-left'}
            visibleToasts={1}
            className='!z-[9999999999]'
          />
        </TooltipProvider>
      </QueryClientProvider>
    </AuthProvider>
  )
}
